import { classNames } from '@/lib/generic-utils';
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import _ from 'lodash';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useMemo } from 'react';

type PageNavProps = {
    currentPage: number;
    totalPages: number;
}

const PageNav = ({ currentPage, totalPages, }: PageNavProps) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const router = useRouter();

    const createPageUrl = (page: number) => {
        const params = new URLSearchParams(searchParams||[]);
        params.set('page', page.toString());
        return `${pathname}?${params.toString()}`;
    };

    const generatePageNumbers = useMemo(() => {
        const ellipsis = '...';
        if (totalPages <= 7) {
            return _.range(1, totalPages + 1);
        }
        if (currentPage <= 4) {
            return [...(_.range(1, 6)), ellipsis, totalPages];
        }
        if (currentPage >= totalPages - 3) {
            return [1, ellipsis, ...(_.range(totalPages - 4, totalPages + 1))];
        }
        return [1, ellipsis, ...(_.range(currentPage - 1, currentPage + 2)), ellipsis, totalPages];
    }, [currentPage, totalPages]);

    const handlePageChange = (page: number) => {
        router.push(createPageUrl(page));
    };

    const buttonClass = (isActive: boolean, isDisabled: boolean) => 
        `p-2 ${isDisabled ? 'text-neutral-400 dark:text-neutral-700 cursor-not-allowed' : isActive ? 'text-black dark:text-white bg-neutral-300 dark:bg-neutral-700' : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 hover:dark:bg-neutral-800'}`;

    return (
        <div className="flex items-center justify-center space-x-2 my-4">
            <button onClick={() => handlePageChange(1)} className={buttonClass(false, currentPage === 1)} disabled={currentPage === 1} aria-label="First page">
                <ChevronDoubleLeftIcon className="w-5 h-5" />
            </button>
            <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} className={buttonClass(false, currentPage === 1)} disabled={currentPage === 1} aria-label="Previous page">
                <ChevronLeftIcon className="w-5 h-5" />
            </button>
            {generatePageNumbers.map((page, index) => (
                <React.Fragment key={index}>
                    {page === '...' ? (
                        <span className={classNames("px-3 py-2", buttonClass(false,false))}>...</span>
                    ) : (
                        <button
                            onClick={() => handlePageChange(page as number)}
                            className={buttonClass(currentPage === page, false)}
                            aria-label={`Page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    )}
                </React.Fragment>
            ))}

            <button onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} className={buttonClass(false, currentPage === totalPages)} disabled={currentPage === totalPages} aria-label="Next page">
                <ChevronRightIcon className="w-5 h-5" />
            </button>
            <button onClick={() => handlePageChange(totalPages)} className={buttonClass(false, currentPage === totalPages)} disabled={currentPage === totalPages} aria-label="Last page">
                <ChevronDoubleRightIcon className="w-5 h-5" />
            </button>

        </div>
    );
};

export default PageNav;